import { useEffect, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { GENERAL_INFO_TITLE, SETTINGS_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { OrganizationT } from 'shared/models/organizations';
import api from 'utils/api';

import validator from './OrganizationsManageData.validator';

type FormValues = {
  displayName: string;
  description: string;
  deviceLimit: string;
  poolLimit?: string;
  sourceLimit?: string;
  enableEncryption: boolean;
  xxteaKeyToken: string;
};
function OrganizationsManageData() {
  const history = useHistory();
  const { organizationName } = useParams() as RouterParams;
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    reset,
    control,
    formState: {
      errors,
      isDirty,
    },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: validator,
  });
  const isEdit = organizationName !== undefined;
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnableEncryption, setIsEnableEncryption] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      api.organizations.getOne(organizationName)
        .then((resp: Response) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                toast.error(error?.message);
                setIsLoading(false);
              });
          } else {
            resp.json()
              .then((data: OrganizationT) => {
                reset(data as any);
                setIsEnableEncryption(data.enableEncryption);
                setIsLoading(false);
              });
          }
        });
    }
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (formData: FormValues) => {
    const newData: FormValues = {
      ...formData,
      poolLimit: formData.poolLimit || undefined, // undefined if empty ""
      sourceLimit: formData.sourceLimit || undefined,
    };
    setIsSubmitting(true);
    const request = isEdit
      ? api.organizations.update(organizationName, newData)
      : api.organizations.register(newData);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              setIsSubmitting(false);
              const message = error?.message;
              if (message === 'validate xxtea key token: get jwt claims: invalid authentication token') {
                setError('xxteaKeyToken', { message: 'Invalid token' });
                return;
              }
              if (message.includes('exceed the device limit of the instance')) {
                setError('deviceLimit', { message: `Error: ${message}` });
              }
              toast.error(message);
            });
        } else {
          resp.json()
            .then(() => {
              toast.success(`Organization has been ${isEdit ? 'updated' : 'created'}`);
              setIsSubmitting(false);
              history.push(`/organizations${isEdit ? `/${organizationName}` : ''}`);
            });
        }
      });
  };

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="title__header">
        <h1>{ `Organization · ${isEdit ? 'Edit' : 'Create'} `}</h1>
        <div className="title__header--actions">
          <ButtonSubmitData
            isEdit
            isSubmitting={isSubmitting}
            disabled={isEdit && !isDirty}
          />
        </div>
      </div>
      <div className="form scroll">
        <div className="form__section">
          <h3>{ GENERAL_INFO_TITLE }</h3>
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Display Name"
            register={register('displayName')}
            errorField={errors.displayName}
          />
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            multiline
            maxRows={2}
            label="Description"
            register={register('description')}
            errorField={errors.description}
          />
          <InputForm.Checkbox
            name="enableEncryption"
            sx={{ ml: 0.5, mt: 1 }}
            label="Enable Encryption"
            control={control}
            defaultChecked={getValues().enableEncryption}
            onChange={() => {
              if (isEnableEncryption) {
                setValue('xxteaKeyToken', '');
              }
              setIsEnableEncryption(!isEnableEncryption);
            }}
            helpText="Activates uplink and downlink packet encryption"
          />
          {
            isEnableEncryption && (
              <InputForm.TextField
                fullWidth
                sx={{ m: 1 }}
                multiline
                maxRows={4}
                label="Encryption Key Token"
                register={register('xxteaKeyToken')}
                errorField={errors.xxteaKeyToken}
              />
            )
          }
        </div>
        <div className="form__section">
          <h3>{ SETTINGS_TITLE }</h3>
          <div className="fields-side-line">
            <InputForm.TextField
              fullWidth
              sx={{ m: 1 }}
              label="Maximum number of devices"
              register={register('deviceLimit')}
              errorField={errors.deviceLimit}
            />
            <InputForm.TextField
              fullWidth
              sx={{ m: 1 }}
              label="Maximum numbers of IP networks"
              register={register('poolLimit')}
              errorField={errors.poolLimit}
              helperText="Default value: unlimited"
            />
            <InputForm.TextField
              fullWidth
              sx={{ m: 1 }}
              label="Maximum numbers of sources"
              register={register('sourceLimit')}
              errorField={errors.sourceLimit}
              helperText="Default value: unlimited"
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export default OrganizationsManageData;
